import { useCallback, useEffect, useRef, useState } from "react";
import { createScript, previewScript } from "../services/script.service.js";
import { reportFrontendError } from "../utils/errorReporter.js";

const getScriptErrorLocation = (error) => {
  const line = error?.error?.line ?? error?.line;
  const char = error?.error?.char ?? error?.char;

  if (typeof line === "number" && typeof char === "number") {
    return { line, char };
  }

  return null;
};

const useScriptExecution = ({
  animationId,
  script,
  pushLog,
  clearLogs,
  onExecuted,
  onExecutedRefresh,
  onExecutionError,
  onExecutionSuccess,
  onPreview,
  previewSummaryMode = "none",
}) => {
  const [isExecuting, setIsExecuting] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const previewInFlightRef = useRef(false);

  const pushLogRef = useRef(pushLog);
  const onPreviewRef = useRef(onPreview);
  const onExecutionErrorRef = useRef(onExecutionError);
  const onExecutionSuccessRef = useRef(onExecutionSuccess);
  const onExecutedRef = useRef(onExecuted);
  const onExecutedRefreshRef = useRef(onExecutedRefresh);
  const clearLogsRef = useRef(clearLogs);

  useEffect(() => {
    pushLogRef.current = pushLog;
  }, [pushLog]);

  useEffect(() => {
    onPreviewRef.current = onPreview;
  }, [onPreview]);

  useEffect(() => {
    onExecutionErrorRef.current = onExecutionError;
  }, [onExecutionError]);

  useEffect(() => {
    onExecutionSuccessRef.current = onExecutionSuccess;
  }, [onExecutionSuccess]);

  useEffect(() => {
    onExecutedRef.current = onExecuted;
  }, [onExecuted]);

  useEffect(() => {
    onExecutedRefreshRef.current = onExecutedRefresh;
  }, [onExecutedRefresh]);

  useEffect(() => {
    clearLogsRef.current = clearLogs;
  }, [clearLogs]);

  const preview = useCallback(async () => {
    if (!animationId || !script) return;
    if (previewInFlightRef.current) return;

    previewInFlightRef.current = true;
    setIsPreviewing(true);
    try {
      clearLogsRef.current?.();
      const result = await previewScript(animationId, script, {
        summaryMode: previewSummaryMode,
      });

      if (result?.previewData) {
        onPreviewRef.current?.({
          previewData: result.previewData,
          visualSummary: result.visualSummary || null,
        });
      }

      if (result?.printLogs?.length) {
        result.printLogs
          .filter((e) => e.type !== "error")
          .forEach((entry) => {
            pushLogRef.current?.(entry.type, entry.message || "", {
              line: entry.line,
              char: entry.char,
              time: entry.time,
            });
          });
      }
    } catch (err) {
      const location = getScriptErrorLocation(err);
      console.log("Script preview error", { error: err, location });
      if (location) {
        const locationText = ` (line ${location.line}, char ${location.char})`;
        pushLogRef.current?.(
          "error",
          `${err.message || "Script execution failed"}${locationText}`,
          { line: location.line, char: location.char },
        );
        onExecutionErrorRef.current?.(location);
      }
    } finally {
      previewInFlightRef.current = false;
      setIsPreviewing(false);
    }
  }, [animationId, previewSummaryMode, script]);

  const execute = useCallback(async () => {
    if (!animationId) {
      pushLogRef.current?.(
        "error",
        "Save animation metadata first to create an animation ID",
      );
      return;
    }

    setIsExecuting(true);
    clearLogsRef.current?.();
    pushLogRef.current?.("info", "Executing script...");
    try {
      const result = await createScript(animationId, script);

      if (result?.printLogs?.length) {
        result.printLogs.forEach((entry) => {
          pushLogRef.current?.(entry.type, entry.message || "", {
            line: entry.line,
            char: entry.char,
            time: entry.time,
          });
        });
      }
      if (result?.printLogs?.[result.printLogs.length - 1]?.type === "error") {
        return;
      }

      pushLogRef.current?.("success", "Script executed successfully");
      onExecutionSuccessRef.current?.();
      await onExecutedRefreshRef.current?.();
      await onExecutedRef.current?.();
    } catch (error) {
      const location = getScriptErrorLocation(error);
      if (location) {
        const locationText = ` (line ${location.line}, char ${location.char})`;
        pushLogRef.current?.(
          "error",
          `${error.message || "Script execution failed"}${locationText}`,
          { line: location.line, char: location.char },
        );
        onExecutionErrorRef.current?.(location);
      } else {
        reportFrontendError({
          error,
          name: error?.name || "ScriptExecutionUnexpectedError",
          message: error?.message || "Unexpected script execution failure",
          metadata: { type: "script_execution_unexpected", animationId },
        });
        pushLogRef.current?.(
          "error",
          "Unexpected internal error while executing script. The issue has been reported.",
        );
        onExecutionErrorRef.current?.(null);
      }
    } finally {
      setIsExecuting(false);
    }
  }, [animationId, script]);

  return {
    isExecuting,
    isPreviewing,
    preview,
    execute,
  };
};

export default useScriptExecution;
